/**
 * ScatterPlot Component
 *
 * A small, responsive scatter plot rendered as plain SVG. Follows the same
 * pattern as StackedBarChart: D3 is used only for the maths (scales, ticks,
 * formatting) while React renders the resulting <circle>/<text> elements.
 *
 * Designed to be rendered many times as "small multiples" (one plot per
 * country) in the routine surveillance graph view: x is time, y is value.
 */

import { JSX, useMemo, useState } from "react"
import { scaleLinear, scaleSqrt, scaleTime, extent, max, timeFormat, format } from "d3"

/** A single point to plot. */
export interface ScatterPoint {
  /** X position as a time value (the row's start_date). */
  date: Date
  /** Y position as a numeric value (the row's parsed value). */
  value: number
  /**
   * Optional magnitude used to size the dot (here: the number of days the data
   * point covers, end_date - start_date). Larger values render as larger dots.
   * When omitted (or non-positive), the dot falls back to the default radius.
   */
  sizeValue?: number
  /**
   * Optional stable identity for the point, used to link a dot to an external
   * row (e.g. a table row) for selection/highlighting. When set, clicking the
   * dot reports this id via onSelect, and passing it as selectedId emphasises
   * the dot. Points without an id are still plotted but are not selectable.
   */
  id?: number
  /**
   * Optional per-point fill colour (any CSS colour string). When set, this dot
   * is drawn in this colour instead of the plot-wide dotColor — used to colour
   * dots by a grouping (e.g. category combination). When omitted, the dot falls
   * back to the plot-wide dotColor.
   */
  color?: string
  /**
   * Optional descriptive label for the point, shown in the hover tooltip
   * beneath the value/date/day-span — used to surface the point's category
   * combination (the same non-default columns as the colour legend). Omitted
   * when there is nothing extra to describe.
   */
  label?: string
}

// Dot radius bounds (px, in the chart's fixed viewBox coordinate space). The
// smallest sized dot uses MIN and the largest uses MAX; unsized dots use
// DEFAULT. Kept small so dense plots stay legible. Exported so a size legend
// can reproduce the exact radius scale (see dotRadiusForSizeValue).
export const DOT_RADIUS_MIN = 1.25
export const DOT_RADIUS_MAX = 4.5
export const DOT_RADIUS_DEFAULT = 1.5

/**
 * Compute the rendered dot radius (in viewBox px) for a given day-span value,
 * using the SAME sqrt-area scale the plot uses so a legend can draw
 * pixel-accurate reference dots.
 *
 * scaleSqrt is used so a dot's AREA (not its radius) is proportional to the
 * magnitude — the way people read circle size. A non-positive sizeValue or a
 * non-positive maxSizeValue yields the default radius (matching how the plot
 * treats unsized points / a plot with no duration data).
 *
 * @param sizeValue - The day span the dot encodes.
 * @param maxSizeValue - The largest day span across the page (top of the scale
 *        domain); pass the same value used for the plots so sizes match.
 * @param dotRadiusScale - The plot's radius multiplier (defaults to 1). Values
 *        <= 0 are treated as 1, matching ScatterPlot.
 * @returns The radius in viewBox px.
 */
export function dotRadiusForSizeValue(
  sizeValue: number,
  maxSizeValue: number,
  dotRadiusScale: number = 1
): number {
  const radiusMultiplier: number = dotRadiusScale > 0 ? dotRadiusScale : 1
  // No usable magnitude to size against → the default (unsized) radius.
  if (sizeValue <= 0 || maxSizeValue <= 0) {
    return DOT_RADIUS_DEFAULT * radiusMultiplier
  }
  const scale = scaleSqrt()
    .domain([0, maxSizeValue])
    .range([DOT_RADIUS_MIN * radiusMultiplier, DOT_RADIUS_MAX * radiusMultiplier])
  return scale(sizeValue)
}

/** Props for the ScatterPlot component. */
export interface ScatterPlotProps {
  /** The points to plot. */
  points: ScatterPoint[]
  /** Fixed rendered height in pixels. Width is responsive via the viewBox. */
  height?: number
  /** Fill colour for the plotted dots. Defaults to the theme primary colour. */
  dotColor?: string
  /**
   * Optional override for the top of the dot-size (radius) scale's domain. When
   * provided, dots are sized against this magnitude instead of the plot's own
   * largest sizeValue — pass the same value to every plot to make dot sizes
   * comparable across a page of small multiples. When omitted, each plot scales
   * dots relative to its own maximum.
   */
  maxSizeValue?: number
  /**
   * Width of the chart's internal coordinate space (the SVG viewBox width). The
   * SVG scales to fill its container width, so this controls the width-to-height
   * proportion of the drawing: a wider viewBox (relative to height) yields a
   * wider, less letterboxed plot. Small-multiple cells use the default; a
   * full-width plot should pass a value proportional to its container so it
   * fills the page rather than sitting in a narrow, tall box. Defaults to
   * DEFAULT_VIEWBOX_WIDTH.
   */
  viewBoxWidth?: number
  /**
   * Multiplier applied to all dot radii (min, max, and the unsized default).
   * Defaults to 1 (the base sizes tuned for small-multiple cells). A larger
   * value enlarges every dot proportionally — useful for a full-page plot where
   * the base dots would look too small. Values <= 0 are ignored (treated as 1).
   */
  dotRadiusScale?: number
  /**
   * The id of the currently selected point (see ScatterPoint.id), or null when
   * nothing is selected. The matching dot is emphasised (enlarged, opaque,
   * accent colour). Enables linking the plot's selection to an external view
   * such as a data table.
   */
  selectedId?: number | null
  /**
   * Called with a point's id when its dot is clicked. Only fires for points
   * that carry an id. Omit to make dots non-interactive (click does nothing).
   */
  onSelect?: (id: number) => void
  /**
   * Optional shared x-axis (time) domain [min, max]. When provided, the plot
   * uses this instead of its own points' date extent — pass the same value to
   * every plot to give them a common, comparable time axis. Omit to scale x to
   * this plot's own data.
   */
  xDomainOverride?: [Date, Date]
  /**
   * Optional shared y-axis maximum. When provided, the plot's y-axis runs from
   * 0 to this value instead of the plot's own max — pass the same value to
   * every plot to give them a common, comparable value axis. Omit to scale y to
   * this plot's own data.
   */
  yMaxOverride?: number
}

// Fixed inner margins around the plot area (space for axes and labels).
const MARGIN = { top: 8, right: 10, bottom: 22, left: 40 } as const

// Default width of the chart's coordinate space. The SVG scales to the
// container width via the viewBox, so this only sets the width-to-height
// proportion, not the rendered pixel width. Suited to a small-multiple cell;
// callers rendering a wide, full-page plot should pass a larger viewBoxWidth.
const DEFAULT_VIEWBOX_WIDTH = 320

// Compact formatters for the axis ticks.
const formatYTick = format("~s")
const formatXTick = timeFormat("%Y")
const formatDateFull = timeFormat("%Y-%m-%d")
const formatValueExact = format(",")

/** A resolved axis tick: its display label and pixel position. */
interface AxisTick {
  label: string
  pos: number
}

/** A single dot to render, with its pixel position, radius, and source values. */
interface Dot {
  cx: number
  cy: number
  /** Rendered radius in viewBox px — scaled by sizeValue when provided. */
  r: number
  date: Date
  value: number
  /** The magnitude the radius encodes (day span), or null when unsized. */
  sizeValue: number | null
  /** The point's external identity (row link), or null when not selectable. */
  id: number | null
  /** Per-point fill colour, or null to use the plot-wide dotColor. */
  color: string | null
  /** Descriptive label shown in the tooltip (e.g. category combination), or null. */
  label: string | null
}

/** Tooltip state: the hovered dot and where to draw the tooltip (container px). */
interface TooltipState {
  dot: Dot
  left: number
  top: number
}

/**
 * A responsive scatter plot drawn as plain SVG.
 *
 * D3 computes the time (x) and linear (y) scales and axis ticks; React renders
 * the dots and axes. Hovering a dot shows a tooltip with its date and value.
 * With no points, an empty-state message is rendered instead of an empty plot.
 *
 * @param props - See ScatterPlotProps.
 * @param props.points - The points to plot (date on x, value on y).
 * @param props.height - Rendered height in pixels (default 160).
 * @param props.dotColor - Dot fill colour (default the theme primary colour).
 * @returns The scatter plot as a responsive, accessible SVG with a tooltip.
 */
export default function ScatterPlot({
  points,
  height = 160,
  dotColor = "var(--primary)",
  maxSizeValue: maxSizeValueOverride,
  viewBoxWidth = DEFAULT_VIEWBOX_WIDTH,
  dotRadiusScale = 1,
  selectedId = null,
  onSelect,
  xDomainOverride,
  yMaxOverride,
}: ScatterPlotProps): JSX.Element {
  // The currently hovered dot and where to draw its tooltip, or null.
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)

  // All layout maths run once per points/height change, producing plain lists
  // of dots and axis ticks that React renders directly.
  const { dots, xTicks, yTicks, innerHeight } = useMemo(() => {
    const innerWidth = viewBoxWidth - MARGIN.left - MARGIN.right
    const plotHeight = height - MARGIN.top - MARGIN.bottom

    // X domain: use the shared override when given (so plots share a time axis),
    // otherwise the plot's own min/max date. extent returns [undefined,
    // undefined] for an empty array; fall back to a single-day span so the scale
    // stays valid.
    const [minDate, maxDate] = extent(points, (p) => p.date)
    const xDomain: [Date, Date] =
      xDomainOverride !== undefined
        ? xDomainOverride
        : minDate !== undefined && maxDate !== undefined
          ? [minDate, maxDate]
          : [new Date(), new Date()]

    const xScale = scaleTime().domain(xDomain).range([0, innerWidth]).nice()

    // Y domain from 0 to the max value: use the shared override when given (so
    // plots share a value axis), otherwise the plot's own max. A zero/empty max
    // falls back to 1 so the axis and scale stay valid.
    const localMaxValue = points.reduce((acc, p) => Math.max(acc, p.value), 0)
    const maxValue =
      yMaxOverride !== undefined && yMaxOverride > 0 ? yMaxOverride : localMaxValue
    const yScale = scaleLinear()
      .domain([0, maxValue > 0 ? maxValue : 1])
      .nice()
      .range([plotHeight, 0])

    // Radius scale: map the sizeValue (day span) to a dot radius. scaleSqrt is
    // used deliberately so the dot's AREA — not its radius — is proportional to
    // the magnitude, which is how humans read circle size. The domain runs from
    // 0 to the largest sizeValue; a zero/absent max makes every sized dot fall
    // back to the default radius. Only points that carry a positive sizeValue
    // are sized; the rest use DOT_RADIUS_DEFAULT.
    //
    // When maxSizeValueOverride is provided, it sets the top of the domain so
    // dot sizes are comparable across a page of plots; otherwise each plot uses
    // its own largest sizeValue.
    const localMaxSizeValue: number =
      max(points, (p) => (p.sizeValue !== undefined && p.sizeValue > 0 ? p.sizeValue : 0)) ?? 0
    const maxSizeValue: number =
      maxSizeValueOverride !== undefined && maxSizeValueOverride > 0
        ? maxSizeValueOverride
        : localMaxSizeValue

    const resolvedDots: Dot[] = points.map((p) => {
      const hasSize: boolean = p.sizeValue !== undefined && p.sizeValue > 0
      return {
        cx: xScale(p.date),
        cy: yScale(p.value),
        // Sized dots use the shared sqrt-area scale (dotRadiusForSizeValue);
        // unsized dots (and plots with no duration data) fall back to the
        // default radius inside that helper.
        r: dotRadiusForSizeValue(
          hasSize ? (p.sizeValue as number) : 0,
          maxSizeValue,
          dotRadiusScale
        ),
        date: p.date,
        value: p.value,
        sizeValue: hasSize ? (p.sizeValue as number) : null,
        id: p.id ?? null,
        color: p.color ?? null,
        label: p.label ?? null,
      }
    })

    const resolvedXTicks: AxisTick[] = xScale.ticks(4).map((tickDate) => ({
      label: formatXTick(tickDate),
      pos: xScale(tickDate),
    }))

    const resolvedYTicks: AxisTick[] = yScale.ticks(4).map((tickValue) => ({
      label: formatYTick(tickValue),
      pos: yScale(tickValue),
    }))

    return {
      dots: resolvedDots,
      xTicks: resolvedXTicks,
      yTicks: resolvedYTicks,
      innerHeight: plotHeight,
    }
  }, [points, height, maxSizeValueOverride, viewBoxWidth, dotRadiusScale, xDomainOverride, yMaxOverride])

  /**
   * Update the tooltip position from a pointer event over a dot.
   *
   * Positions the tooltip relative to the wrapping container using the mouse
   * coordinates within it, so it follows the cursor regardless of the SVG's
   * responsive scaling.
   *
   * @param event - The React mouse event on the dot circle.
   * @param dot - The hovered dot.
   */
  function handleDotHover(
    event: React.MouseEvent<SVGCircleElement>,
    dot: Dot
  ): void {
    const container = event.currentTarget.ownerSVGElement?.parentElement
    if (!container) {
      return
    }
    const bounds = container.getBoundingClientRect()
    setTooltip({
      dot,
      left: event.clientX - bounds.left,
      top: event.clientY - bounds.top,
    })
  }

  // With no points there is nothing to plot; show an inline empty state rather
  // than an axes-only chart, which reads as "no data" more clearly.
  if (points.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ height }}
      >
        No data
      </div>
    )
  }

  return (
    <div className="relative w-full">
      <svg
        viewBox={`0 0 ${viewBoxWidth} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label="Scatter plot of value over time"
        className="text-muted-foreground"
      >
        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
          {/* Y-axis gridlines + tick labels. */}
          {yTicks.map((tick) => (
            <g key={`y-${tick.label}-${tick.pos}`}>
              <line
                x1={0}
                x2={viewBoxWidth - MARGIN.left - MARGIN.right}
                y1={tick.pos}
                y2={tick.pos}
                stroke="currentColor"
                strokeOpacity={0.15}
              />
              <text
                x={-6}
                y={tick.pos}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={9}
                fill="currentColor"
              >
                {tick.label}
              </text>
            </g>
          ))}

          {/* X-axis tick labels along the bottom. */}
          {xTicks.map((tick) => (
            <text
              key={`x-${tick.label}-${tick.pos}`}
              x={tick.pos}
              y={innerHeight + 14}
              textAnchor="middle"
              fontSize={9}
              fill="currentColor"
            >
              {tick.label}
            </text>
          ))}

          {/* Data points. Radius encodes the day span (larger = longer period).
              The selected dot is skipped here and re-drawn last (below) so it
              always sits on top of any overlapping dots. */}
          {dots.map((dot, index) => {
            const isSelected: boolean = dot.id !== null && dot.id === selectedId
            if (isSelected) {
              return null
            }
            const selectable: boolean = dot.id !== null && onSelect !== undefined
            // Per-point colour when provided (e.g. colouring by category
            // group), otherwise the plot-wide dotColor.
            const fill: string = dot.color ?? dotColor
            return (
              <circle
                key={index}
                cx={dot.cx}
                cy={dot.cy}
                r={dot.r}
                fill={fill}
                fillOpacity={0.6}
                stroke={fill}
                strokeOpacity={0.9}
                strokeWidth={0.5}
                className={selectable ? "cursor-pointer" : undefined}
                onMouseMove={(event) => handleDotHover(event, dot)}
                onMouseLeave={() => setTooltip(null)}
                onClick={
                  selectable ? () => onSelect?.(dot.id as number) : undefined
                }
              />
            )
          })}

          {/* Selected dot, drawn last so it is never occluded. Emphasised with
              a larger radius, full opacity, and the accent colour. */}
          {dots
            .filter((dot) => dot.id !== null && dot.id === selectedId)
            .map((dot) => (
              <circle
                key={`selected-${dot.id}`}
                cx={dot.cx}
                cy={dot.cy}
                // Enlarge relative to the dot's own radius so the emphasis is
                // visible even for small dots, with a floor for tiny ones.
                r={Math.max(dot.r * 1.6, dot.r + 2)}
                fill="var(--accent-foreground, currentColor)"
                fillOpacity={0.9}
                stroke="var(--background)"
                strokeWidth={1}
                className={onSelect !== undefined ? "cursor-pointer" : undefined}
                onMouseMove={(event) => handleDotHover(event, dot)}
                onMouseLeave={() => setTooltip(null)}
                onClick={
                  onSelect !== undefined
                    ? () => onSelect?.(dot.id as number)
                    : undefined
                }
              />
            ))}
        </g>
      </svg>

      {/* Hover tooltip — an absolutely-positioned overlay following the cursor. */}
      {tooltip && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-md border bg-popover px-2 py-1 text-xs shadow-md"
          style={{ left: tooltip.left, top: tooltip.top - 8 }}
          role="tooltip"
        >
          <div className="font-medium">{formatValueExact(tooltip.dot.value)}</div>
          <div className="mt-0.5 text-muted-foreground">
            {formatDateFull(tooltip.dot.date)}
          </div>
          {tooltip.dot.sizeValue !== null && (
            <div className="mt-0.5 text-muted-foreground">
              {formatValueExact(tooltip.dot.sizeValue)} day
              {tooltip.dot.sizeValue === 1 ? "" : "s"}
            </div>
          )}
          {/* The point's category combination (non-default differing columns),
              when provided. Separated from the numeric lines by a hairline. */}
          {tooltip.dot.label !== null && (
            <div className="mt-1 border-t pt-1 text-muted-foreground">
              {tooltip.dot.label}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
